import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRole } from '../contexts/RoleContext';
import { useGame } from '../contexts/GameContext';
import { 
  collection, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc,
  writeBatch,
  deleteDoc,
  addDoc,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { userUpdateProfile, blockUserLogin, deleteUser as apiDeleteUser } from '../api/userApi';
import * as readApi from '../api/readApi';
import { getAdminAllData, createApartment as apiCreateApartment, updateApartment as apiUpdateApartment, deleteApartment as apiDeleteApartment } from '../api/adminApi';
import { formatSkillLevel } from '../utils/formatSkillLevel';
import { expandRangeExpression } from '../utils/flatNumberRanges';
import { isAdminRole, isSuperAdmin } from '../constants/roles';
import { PLAN_IDS, PLANS, getPlanById } from '../constants/plans';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../services/auditLogService';
import EntityProfileFields, { getEntityProfileDefaults } from '../components/EntityProfileFields';
import { getProfileForGame } from '../utils/gameProfile';
import { useAlert } from '../contexts/AlertContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import './AdminPanel.css';

export default function AdminPanel() {
  const { currentUser, refreshUserProfile } = useAuth();
  const { showAlert, showConfirmPromise, showToast } = useAlert();
  const { startImpersonating } = useImpersonation();
  const { isAdmin: roleIsAdmin, isSuperAdmin: isSuperAdminRole, switchRole } = useRole();
  const { gameId: adminGameId } = useGame();
  const canManageAdmins = isSuperAdminRole;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [apartments, setApartments] = useState([]);
  const [apartmentName, setApartmentName] = useState('');
  const [apartmentShortCode, setApartmentShortCode] = useState('');
  const [apartmentFlats, setApartmentFlats] = useState(''); // comma-separated
  const [apartmentSaving, setApartmentSaving] = useState(false);
  const [editingApartmentId, setEditingApartmentId] = useState(null);
  const [rangeExpression, setRangeExpression] = useState('');
  const [singleFlatToAdd, setSingleFlatToAdd] = useState('');
  const [editingFlatIndex, setEditingFlatIndex] = useState(null);
  const [editingFlatValue, setEditingFlatValue] = useState('');
  const [editingApartmentAdminIds, setEditingApartmentAdminIds] = useState([]);
  const [apartmentProfileFields, setApartmentProfileFields] = useState({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' });
  const [apartmentAdminEmailToAdd, setApartmentAdminEmailToAdd] = useState('');
  const [editingRegUser, setEditingRegUser] = useState(null);
  const [editingRegTournamentId, setEditingRegTournamentId] = useState('');
  const [editingRegSingles, setEditingRegSingles] = useState(false);
  const [editingRegDoubles, setEditingRegDoubles] = useState(false);
  const [editingRegSaving, setEditingRegSaving] = useState(false);
  const [editingProfileUser, setEditingProfileUser] = useState(null);
  const [profileEditDisplayName, setProfileEditDisplayName] = useState('');
  const [profileEditSkillLevel, setProfileEditSkillLevel] = useState('average');
  const [profileEditYears, setProfileEditYears] = useState('');
  const [profileEditSaving, setProfileEditSaving] = useState(false);
  const [matches, setMatches] = useState([]);
  const [userDetailModalUser, setUserDetailModalUser] = useState(null);
  const [editingUserPlanId, setEditingUserPlanId] = useState(null);
  const [savingUserPlan, setSavingUserPlan] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkPlanId, setBulkPlanId] = useState(PLAN_IDS.BASIC);
  const [bulkPlanSaving, setBulkPlanSaving] = useState(false);
  const [quickLinksOpen, setQuickLinksOpen] = useState(false);
  const quickLinksRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (quickLinksRef.current && !quickLinksRef.current.contains(e.target)) setQuickLinksOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper: any admin (isAdmin === true | 'admin' | 'super' or legacy isSuperAdmin)
  function isAdminFromData(data) {
    return isAdminRole(data);
  }

  function auditContext() {
    return {
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      displayName: currentUser?.displayName || currentUser?.email
    };
  }

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { profile } = await readApi.getMyProfile();
        const isAdminNow = profile?.isAdmin || profile?.isSuperAdmin;
        if (cancelled) return;
        if (!isAdminNow) {
          navigate('/dashboard');
          return;
        }
        await refreshUserProfile();
        if (!roleIsAdmin) switchRole('admin');
        await fetchAllData();
      } catch (e) {
        if (!cancelled) navigate('/dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Only re-run when user or navigate changes; avoid deps that change after switchRole/refresh (which would re-run and leave loader stuck)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  async function fetchAllData() {
    setLoading(true);
    try {
      const data = await getAdminAllData();
      setUsers(data.users || []);
      setPlayers(data.players || []);
      setMatches(data.matches || []);
      setTournaments(data.tournaments || []);
      setRegistrations(data.registrations || []);
      setApartments(data.apartments || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }

  async function saveApartment() {
    const name = apartmentName?.trim();
    const shortCode = (apartmentShortCode || '').trim();
    const flatNumbers = apartmentFlats.split(',').map(s => s.trim()).filter(Boolean);
    if (!name) {
      showAlert('Enter apartment name.', { variant: 'warning' });
      return;
    }
    if (flatNumbers.length === 0) {
      showAlert('Enter at least one flat number (comma-separated).', { variant: 'warning' });
      return;
    }
    setApartmentSaving(true);
    try {
      const adminIds = Array.isArray(editingApartmentAdminIds) ? editingApartmentAdminIds : [];
      const payload = {
        name,
        flatNumbers,
        adminIds,
        shortCode: shortCode || '',
        avatarURL: (apartmentProfileFields.avatarURL || '').trim() || null,
        description: (apartmentProfileFields.description || '').trim() || null,
        phone: (apartmentProfileFields.phone || '').trim() || null,
        address: (apartmentProfileFields.address || '').trim() || null,
        mapUrl: (apartmentProfileFields.mapUrl || '').trim() || null
      };
      if (editingApartmentId) {
        await apiUpdateApartment(editingApartmentId, payload);
        showToast('Apartment updated.', { variant: 'success' });
      } else {
        await apiCreateApartment(payload);
        showToast('Apartment added.', { variant: 'success' });
      }
      setApartmentName('');
      setApartmentShortCode('');
      setApartmentFlats('');
      setApartmentProfileFields({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' });
      setEditingApartmentId(null);
      setEditingApartmentAdminIds([]);
      await fetchAllData();
      await refreshUserProfile();
    } catch (err) {
      const msg = err?.message || '';
      const isPermission = err?.code === 'permission-denied' || /permission|insufficient/i.test(msg);
      showAlert(isPermission
        ? 'Insufficient permissions: only admins can add or edit apartments. Have an existing admin promote you under Admin → Users (Make Admin).'
        : 'Failed to save: ' + msg, { variant: 'error' });
    } finally {
      setApartmentSaving(false);
    }
  }

  async function deleteApartment(id) {
    const ok = await showConfirmPromise('Delete this apartment? Users who selected it will keep it until they change.', { title: 'Delete apartment', destructive: true });
    if (!ok) return;
    try {
      await apiDeleteApartment(id);
      await fetchAllData();
      if (editingApartmentId === id) {
        setEditingApartmentId(null);
        setApartmentName('');
        setApartmentShortCode('');
        setApartmentFlats('');
      }
    } catch (err) {
      showAlert('Failed to delete: ' + (err.message || err), { variant: 'error' });
    }
  }

  function startEditApartment(apt) {
    setEditingApartmentId(apt.id);
    setApartmentName(apt.name || '');
    setApartmentShortCode(apt.shortCode || '');
    setApartmentFlats((apt.flatNumbers || []).join(', '));
    setEditingApartmentAdminIds(Array.isArray(apt.adminIds) ? [...apt.adminIds] : []);
    setApartmentProfileFields(getEntityProfileDefaults(apt));
    setRangeExpression('');
    setSingleFlatToAdd('');
    setEditingFlatIndex(null);
  }

  function addApartmentAdminByEmail() {
    const email = (apartmentAdminEmailToAdd || '').trim().toLowerCase();
    if (!email) return;
    const u = users.find(x => (x.email || '').toLowerCase() === email);
    if (!u) {
      showAlert(`No user found with email ${email}`, { variant: 'warning' });
      return;
    }
    if (editingApartmentAdminIds.includes(u.id)) {
      showToast('Already an apartment admin.', { variant: 'info' });
      return;
    }
    setEditingApartmentAdminIds([...editingApartmentAdminIds, u.id]);
    setApartmentAdminEmailToAdd('');
  }

  function removeApartmentAdmin(uid) {
    setEditingApartmentAdminIds(editingApartmentAdminIds.filter(id => id !== uid));
  }

  function getFlatList() {
    return apartmentFlats.split(',').map(s => s.trim()).filter(Boolean);
  }

  function setFlatsFromList(arr) {
    setApartmentFlats(arr.join(', '));
  }

  function handleAddFromRange() {
    const current = getFlatList();
    const expanded = expandRangeExpression(rangeExpression);
    const combined = [...current];
    expanded.forEach(v => { if (!combined.includes(v)) combined.push(v); });
    setFlatsFromList(combined);
    if (expanded.length) setRangeExpression('');
  }

  function handleReplaceWithRange() {
    const expanded = expandRangeExpression(rangeExpression);
    setFlatsFromList(expanded);
    setRangeExpression('');
  }

  function handleRemoveFlat(index) {
    const list = getFlatList();
    setFlatsFromList(list.filter((_, i) => i !== index));
    if (editingFlatIndex === index) setEditingFlatIndex(null);
    else if (editingFlatIndex != null && editingFlatIndex > index) setEditingFlatIndex(editingFlatIndex - 1);
  }

  function handleAddSingleFlat() {
    const v = singleFlatToAdd.trim();
    if (!v) return;
    const list = getFlatList();
    if (list.includes(v)) return;
    setFlatsFromList([...list, v]);
    setSingleFlatToAdd('');
  }

  function startEditFlat(index) {
    const list = getFlatList();
    setEditingFlatIndex(index);
    setEditingFlatValue(list[index] || '');
  }

  function saveEditFlat() {
    if (editingFlatIndex == null) return;
    const list = getFlatList();
    const v = editingFlatValue.trim();
    if (v && !list.some((f, i) => i !== editingFlatIndex && f === v)) {
      const next = [...list];
      next[editingFlatIndex] = v;
      setFlatsFromList(next);
    }
    setEditingFlatIndex(null);
    setEditingFlatValue('');
  }

  async function toggleAdminStatus(userId, isCurrentlyAdmin) {
    const ok = await showConfirmPromise(`Are you sure you want to ${!isCurrentlyAdmin ? 'promote' : 'remove'} this user ${!isCurrentlyAdmin ? 'to' : 'from'} admin?`, { title: 'Confirm' });
    if (!ok) {
      return;
    }
    try {
      const newValue = !isCurrentlyAdmin;
      await userUpdateProfile(userId, currentUser.uid, { isAdmin: newValue });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, userId, auditContext(), { isAdmin: newValue }, null);
      showToast(`User ${!isCurrentlyAdmin ? 'promoted to' : 'removed from'} admin successfully!`, { variant: 'success' });
      await fetchAllData(); // Refresh all data
    } catch (err) {
      console.error('Error updating admin status:', err);
      showAlert('Error updating admin status: ' + err.message, { variant: 'error' });
    }
  }

  async function toggleApartmentApproved(userId, currentApproved) {
    try {
      await userUpdateProfile(userId, currentUser.uid, { apartmentApproved: !currentApproved });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, userId, auditContext(), { apartmentApproved: !currentApproved }, null);
      showToast(currentApproved ? 'User unapproved for apartment.' : 'User approved for apartment.', { variant: 'success' });
      await fetchAllData();
    } catch (err) {
      console.error('Error toggling apartment approval:', err);
      showAlert('Error: ' + (err.message || err), { variant: 'error' });
    }
  }

  function openEditRegistration(user) {
    const forGame = tournaments.filter((t) => (t.gameId || 'table-tennis') === adminGameId);
    const firstTournamentId = forGame[0]?.id || tournaments[0]?.id || '';
    const regsForFirst = registrations.filter(r => r.playerId === user.id && r.tournamentId === firstTournamentId);
    setEditingRegUser(user);
    setEditingRegTournamentId(firstTournamentId);
    setEditingRegSingles(regsForFirst.some(r => r.type === 'singles'));
    setEditingRegDoubles(regsForFirst.some(r => r.type === 'doubles'));
  }

  function closeEditRegistration() {
    setEditingRegUser(null);
    setEditingRegTournamentId('');
    setEditingRegSingles(false);
    setEditingRegDoubles(false);
  }

  function openEditProfile(user) {
    setEditingProfileUser(user);
    setProfileEditDisplayName((user.displayName || user.email || '').trim());
    const gameProfile = getProfileForGame(user, adminGameId);
    setProfileEditSkillLevel(gameProfile.skillLevel || 'average');
    setProfileEditYears(String(gameProfile.yearsOfExperience ?? ''));
  }

  function closeEditProfile() {
    setEditingProfileUser(null);
    setProfileEditDisplayName('');
    setProfileEditSkillLevel('average');
    setProfileEditYears('');
  }

  async function saveProfileEdit() {
    if (!editingProfileUser) return;
    const userId = editingProfileUser.id;
    const newDisplayName = (profileEditDisplayName || '').trim();
    const newSkillLevel = profileEditSkillLevel || 'average';
    const newYears = parseInt(profileEditYears, 10);
    const yearsValid = !isNaN(newYears) && newYears >= 0 && newYears <= 50;
    if (!yearsValid) {
      showAlert('Years of experience must be between 0 and 50.', { variant: 'warning' });
      return;
    }
    setProfileEditSaving(true);
    try {
      const beforeGame = getProfileForGame(editingProfileUser, adminGameId);
      const before = {
        displayName: (editingProfileUser.displayName || editingProfileUser.email || '').trim(),
        skillLevel: beforeGame.skillLevel || 'average',
        yearsOfExperience: beforeGame.yearsOfExperience ?? ''
      };
      const after = {
        displayName: newDisplayName,
        skillLevel: newSkillLevel,
        yearsOfExperience: newYears
      };
      const changes = {};
      if (String(before.displayName) !== String(after.displayName)) {
        changes.displayName = { before: before.displayName, after: after.displayName };
      }
      if (String(before.skillLevel) !== String(after.skillLevel)) {
        changes.skillLevel = { before: before.skillLevel, after: after.skillLevel };
      }
      if (Number(before.yearsOfExperience) !== Number(after.yearsOfExperience)) {
        changes.yearsOfExperience = { before: before.yearsOfExperience, after: after.yearsOfExperience };
      }
      if (Object.keys(changes).length === 0) {
        showToast('No changes to save.', { variant: 'info' });
        setProfileEditSaving(false);
        return;
      }
      const userRef = doc(db, 'users', userId);
      const existing = (await getDoc(userRef)).data() || {};
      const existingGameProfiles = existing.gameProfiles || {};
      const mergedGameProfiles = {
        ...existingGameProfiles,
        [adminGameId]: {
          ...(existingGameProfiles[adminGameId] || {}),
          skillLevel: after.skillLevel,
          yearsOfExperience: after.yearsOfExperience,
        },
      };
      await userUpdateProfile(userId, currentUser.uid, {
        displayName: after.displayName || null,
        skillLevel: after.skillLevel,
        yearsOfExperience: after.yearsOfExperience,
        gameProfiles: mergedGameProfiles,
      });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, userId, auditContext(), changes, null);
      const adminName = (currentUser?.displayName || currentUser?.email || 'Admin').trim();
      await addDoc(collection(db, 'profileEditLogs'), {
        userId,
        editedBy: currentUser.uid,
        editedByName: adminName,
        editedAt: Timestamp.now(),
        changes
      });
      showToast('Profile updated. Edit has been logged.', { variant: 'success' });
      closeEditProfile();
      await fetchAllData();
    } catch (err) {
      console.error('Error saving profile edit:', err);
      showAlert('Error saving profile: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setProfileEditSaving(false);
    }
  }

  async function saveUserRegistration() {
    if (!editingRegUser || (!editingRegSingles && !editingRegDoubles)) {
      showAlert('Select at least one of Singles or Doubles.', { variant: 'warning' });
      return;
    }
    if (!editingRegTournamentId) {
      showAlert('Select a tournament.', { variant: 'warning' });
      return;
    }
    setEditingRegSaving(true);
    try {
      const skillLevel = editingRegUser.skillLevel || 'average';
      const skillLevelValue = skillLevel === 'beginner' ? 1 : skillLevel === 'average' ? 2 : 3;
      const playerEmail = editingRegUser.email || '';
      const existing = registrations.filter(
        r => r.playerId === editingRegUser.id && r.tournamentId === editingRegTournamentId
      );
      const hasSingles = existing.some(r => r.type === 'singles');
      const hasDoubles = existing.some(r => r.type === 'doubles');

      if (editingRegSingles && !hasSingles) {
        const ref = await addDoc(collection(db, 'registrations'), {
          tournamentId: editingRegTournamentId,
          playerId: editingRegUser.id,
          playerEmail,
          type: 'singles',
          skillLevel,
          skillLevelValue,
          registeredAt: new Date(),
          status: 'approved'
        });
        await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.REGISTRATION, ref.id, auditContext(), { type: 'singles', status: 'approved' }, { tournamentId: editingRegTournamentId });
      }
      if (!editingRegSingles && hasSingles) {
        const toDelete = existing.find(r => r.type === 'singles');
        if (toDelete) {
          await deleteDoc(doc(db, 'registrations', toDelete.id));
          await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.REGISTRATION, toDelete.id, auditContext(), null, { tournamentId: editingRegTournamentId });
        }
      }
      if (editingRegDoubles && !hasDoubles) {
        const ref = await addDoc(collection(db, 'registrations'), {
          tournamentId: editingRegTournamentId,
          playerId: editingRegUser.id,
          playerEmail,
          type: 'doubles',
          skillLevel,
          skillLevelValue,
          registeredAt: new Date(),
          status: 'approved',
          teamId: null
        });
        await logAudit(AUDIT_ACTIONS.CREATE, AUDIT_ENTITIES.REGISTRATION, ref.id, auditContext(), { type: 'doubles', status: 'approved' }, { tournamentId: editingRegTournamentId });
      }
      if (!editingRegDoubles && hasDoubles) {
        const toDelete = existing.find(r => r.type === 'doubles');
        if (toDelete) {
          await deleteDoc(doc(db, 'registrations', toDelete.id));
          await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.REGISTRATION, toDelete.id, auditContext(), null, { tournamentId: editingRegTournamentId });
        }
      }
      await fetchAllData();
      closeEditRegistration();
    } catch (err) {
      console.error('Error saving registration:', err);
      showAlert('Error: ' + (err.message || err), { variant: 'error' });
    } finally {
      setEditingRegSaving(false);
    }
  }

  // Reserved for future "set admin by email" UI
  // eslint-disable-next-line no-unused-vars
  async function setAdminByEmail(email) {
    if (!email || !email.trim()) {
      showAlert('Please enter a valid email address', { variant: 'warning' });
      return;
    }

    try {
      const userQuery = query(collection(db, 'users'), where('email', '==', email.trim()));
      const userSnapshot = await getDocs(userQuery);
      
      if (userSnapshot.empty) {
        showAlert(`No user found with email: ${email}`, { variant: 'warning' });
        return;
      }

      const userDoc = userSnapshot.docs[0];
      const userData = userDoc.data();
      
      if (isAdminRole(userData)) {
        showToast(`User ${email} is already an admin.`, { variant: 'info' });
        return;
      }

      const ok = await showConfirmPromise(`Promote ${email} to admin?`, { title: 'Promote to admin' });
      if (!ok) return;

      await updateDoc(doc(db, 'users', userDoc.id), {
        isAdmin: true
      });
      
      showToast(`Successfully promoted ${email} to admin!`, { variant: 'success' });
      await fetchAllData();
    } catch (err) {
      console.error('Error setting admin by email:', err);
      showAlert('Error: ' + err.message, { variant: 'error' });
    }
  }

  async function updateUserPlan(userId, planId) {
    if (!userId || !planId) return;
    setSavingUserPlan(true);
    try {
      await updateDoc(doc(db, 'users', userId), { planId });
      await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, userId, auditContext(), { planId: { before: userDetailModalUser?.planId || PLAN_IDS.BASIC, after: planId } }, null);
      showToast('Plan updated.', { variant: 'success' });
      setEditingUserPlanId(planId);
      await fetchAllData();
      if (userId === currentUser?.uid) await refreshUserProfile();
    } catch (err) {
      console.error('Error updating user plan:', err);
      showAlert('Error updating plan: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setSavingUserPlan(false);
    }
  }

  const filteredUsersForTab = filterData(users);
  function toggleUserSelection(userId) {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }
  function selectAllUsersInTable() {
    setSelectedUserIds(filteredUsersForTab.map((u) => u.id));
  }
  function clearUserSelection() {
    setSelectedUserIds([]);
  }
  const allFilteredSelected =
    filteredUsersForTab.length > 0 && filteredUsersForTab.every((u) => selectedUserIds.includes(u.id));

  async function updateUserPlanBulk(userIds, planId) {
    if (!userIds?.length || !planId) return;
    setBulkPlanSaving(true);
    try {
      const BATCH_SIZE = 500;
      const userById = Object.fromEntries(users.map((u) => [u.id, u]));
      for (let i = 0; i < userIds.length; i += BATCH_SIZE) {
        const chunk = userIds.slice(i, i + BATCH_SIZE);
        const batch = writeBatch(db);
        for (const uid of chunk) {
          batch.update(doc(db, 'users', uid), { planId });
        }
        await batch.commit();
        for (const uid of chunk) {
          const before = userById[uid]?.planId || PLAN_IDS.BASIC;
          await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.USER, uid, auditContext(), { planId: { before, after: planId } }, null);
        }
      }
      showToast(`Plan updated for ${userIds.length} user(s).`, { variant: 'success' });
      setSelectedUserIds([]);
      await fetchAllData();
      if (userIds.includes(currentUser?.uid)) await refreshUserProfile();
    } catch (err) {
      console.error('Error bulk updating plans:', err);
      showAlert('Error updating plans: ' + (err?.message || err), { variant: 'error' });
    } finally {
      setBulkPlanSaving(false);
    }
  }

  async function blockUser(userId) {
    const ok = await showConfirmPromise(
      'Block this user\'s login? They will be signed out and see "Logins from your user is blocked by admin" on next sign-in. You can undo by unblocking (contact support or restore in Firebase Auth).',
      { title: 'Block login', destructive: true }
    );
    if (!ok) return;
    try {
      await blockUserLogin(userId);
      showToast('User login blocked.', { variant: 'success' });
      fetchAllData();
    } catch (err) {
      console.error('Error blocking user:', err);
      showAlert('Error blocking login: ' + (err?.message || err), { variant: 'error' });
    }
  }

  async function deleteUser(userId) {
    const ok = await showConfirmPromise('Are you sure you want to delete this user? They will be blocked from signing in and their profile will be removed. This cannot be undone.', { title: 'Delete user', destructive: true });
    if (!ok) return;
    try {
      await apiDeleteUser(userId);
      showToast('User deleted successfully', { variant: 'success' });
      fetchAllData();
    } catch (err) {
      console.error('Error deleting user:', err);
      showAlert('Error deleting user: ' + (err?.message || err), { variant: 'error' });
    }
  }

  function getFirstLast(item) {
    const name = (item.displayName || item.email?.split('@')[0] || '').trim();
    if (!name) return { firstName: '—', lastName: '—' };
    const parts = name.split(/\s+/);
    if (parts.length === 1) return { firstName: parts[0], lastName: '—' };
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }

  function filterData(data) {
    if (!searchTerm) return data;
    const term = searchTerm.toLowerCase();
    return data.filter(item => {
      if (item.email && item.email.toLowerCase().includes(term)) return true;
      if (item.displayName && item.displayName.toLowerCase().includes(term)) return true;
      const { firstName, lastName } = getFirstLast(item);
      if (firstName !== '—' && firstName.toLowerCase().includes(term)) return true;
      if (lastName !== '—' && lastName.toLowerCase().includes(term)) return true;
      if (item.name && item.name.toLowerCase().includes(term)) return true;
      return false;
    });
  }

  function getRegistrationInfo(userId) {
    const tournamentById = Object.fromEntries(tournaments.map(t => [t.id, t]));
    const userRegs = registrations.filter(r => r.playerId === userId);
    const singles = userRegs.filter(r => r.type === 'singles').map(r => tournamentById[r.tournamentId]?.name || r.tournamentId).filter(Boolean);
    const doubles = userRegs.filter(r => r.type === 'doubles').map(r => tournamentById[r.tournamentId]?.name || r.tournamentId).filter(Boolean);
    return {
      singles: singles.length ? singles.join(', ') : '—',
      doubles: doubles.length ? doubles.join(', ') : '—'
    };
  }

  function getApartmentFlatDisplay(user) {
    if (!user?.flatNumber) return '';
    let code = (user.apartmentShortCode || '').trim();
    if (!code && user.apartmentId) {
      const apt = apartments.find((a) => a.id === user.apartmentId);
      code = (apt?.shortCode || '').trim();
    }
    return code ? `${code}-${user.flatNumber}` : user.flatNumber;
  }

  function getMatchCountForUser(userId) {
    if (!userId) return 0;
    return matches.filter(m => {
      if (m.player1Id === userId || m.player2Id === userId || m.refereeId === userId) return true;
      if ((m.team1Players || []).includes(userId) || (m.team2Players || []).includes(userId)) return true;
      return false;
    }).length;
  }

  const tournamentsForGame = tournaments.filter((t) => (t.gameId || 'table-tennis') === adminGameId);
  const pendingForGame = adminGameId
    ? registrations.filter((r) => {
        const t = tournaments.find((x) => x.id === r.tournamentId);
        return t && (t.gameId || 'table-tennis') === adminGameId && r.status === 'pending_approval';
      })
    : registrations.filter((r) => r.status === 'pending_approval');

  if (loading) {
    return <div className="loading">Loading admin panel...</div>;
  }

  return (
    <div className="admin-panel-container">
      <header className="admin-header">
        <div>
          <h1 className="page-title">Admin Panel</h1>
          <p className="page-subtitle">Users, players, apartments, pending registrations. Game: {adminGameId || 'table-tennis'} (switch in header)</p>
        </div>
        <div className="admin-header-actions">
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary">
            Dashboard
          </button>
          <div className="admin-quick-links" ref={quickLinksRef}>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setQuickLinksOpen((o) => !o)}
              aria-expanded={quickLinksOpen}
              aria-haspopup="true"
            >
              Quick links ▾
            </button>
            {quickLinksOpen && (
              <ul className="admin-quick-links-menu" role="menu">
                <li><button type="button" role="menuitem" onClick={() => { navigate('/tournaments'); setQuickLinksOpen(false); }}>Tournaments</button></li>
                <li><button type="button" role="menuitem" onClick={() => { navigate('/tournament/manage'); setQuickLinksOpen(false); }}>Tournament Management</button></li>
                <li><button type="button" role="menuitem" onClick={() => { navigate('/tournament/schedule'); setQuickLinksOpen(false); }}>Schedule management</button></li>
                <li><button type="button" role="menuitem" onClick={() => { navigate('/venues'); setQuickLinksOpen(false); }}>Venue Management</button></li>
                <li><button type="button" role="menuitem" onClick={() => { navigate('/groups'); setQuickLinksOpen(false); }}>Groups</button></li>
                <li><button type="button" role="menuitem" onClick={() => { navigate('/apartment-manage'); setQuickLinksOpen(false); }}>Apartment Manage</button></li>
                {canManageAdmins && <li><button type="button" role="menuitem" onClick={() => { navigate('/admin/tools'); setQuickLinksOpen(false); }}>Tools</button></li>}
                {canManageAdmins && <li><button type="button" role="menuitem" onClick={() => { navigate('/audit-log'); setQuickLinksOpen(false); }}>Audit Log</button></li>}
              </ul>
            )}
          </div>
        </div>
      </header>

      <div className="admin-tabs">
        <button
          type="button"
          className={activeTab === 'users' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('users')}
        >
          Users ({users.length})
        </button>
        <button
          type="button"
          className={activeTab === 'players' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('players')}
        >
          Players ({players.length})
        </button>
        <button
          type="button"
          className={activeTab === 'apartments' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('apartments')}
        >
          Apartments ({apartments.length})
        </button>
        <button
          type="button"
          className={activeTab === 'pending' ? 'admin-tab active' : 'admin-tab'}
          onClick={() => setActiveTab('pending')}
        >
          Pending ({pendingForGame.length})
        </button>
      </div>

      <div className="admin-search">
        <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="admin-content">
        {activeTab === 'users' && (
          <div className="admin-section card-ui">
            <h2 className="admin-section-title">All Users</h2>
            {selectedUserIds.length > 0 && (
              <div className="admin-bulk-bar" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px', padding: '10px 12px', background: 'var(--card-bg, #f8f9fa)', borderRadius: '8px', border: '1px solid var(--border, #e0e0e0)' }}>
                <span style={{ fontWeight: 600 }}>{selectedUserIds.length} selected</span>
                <select
                  value={bulkPlanId}
                  onChange={(e) => setBulkPlanId(e.target.value)}
                  disabled={bulkPlanSaving}
                  className="search-input"
                  style={{ maxWidth: '160px', padding: '6px 10px' }}
                >
                  {PLANS.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => updateUserPlanBulk(selectedUserIds, bulkPlanId)}
                  disabled={bulkPlanSaving}
                  className="btn-small btn-primary"
                >
                  {bulkPlanSaving ? 'Updating…' : 'Update plan'}
                </button>
                <button type="button" onClick={clearUserSelection} className="btn-small btn-secondary">Clear selection</button>
              </div>
            )}
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        checked={filteredUsersForTab.length > 0 && allFilteredSelected}
                        onChange={() => (allFilteredSelected ? clearUserSelection() : selectAllUsersInTable())}
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsersForTab.map(user => {
                    const { firstName, lastName } = getFirstLast(user);
                    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || user.displayName || user.email || '—';
                    const aptFlat = getApartmentFlatDisplay(user);
                    const planId = user.planId || PLAN_IDS.BASIC;
                    const plan = getPlanById(planId);
                    return (
                    <tr key={user.id}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Select ${fullName}`}
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                        />
                      </td>
                      <td>
                        <span>{fullName}</span>
                        {aptFlat && <span style={{ display: 'block', fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{aptFlat}</span>}
                      </td>
                      <td>{user.email || '—'}</td>
                      <td>
                        <span className="plan-badge" title={plan.description}>{plan.name}</span>
                      </td>
                      <td>
                        {user.loginBlocked ? (
                          <span className="user-badge" style={{ background: '#dc3545', color: '#fff' }} title="Login blocked by admin">Blocked</span>
                        ) : user.isAdmin === 'super' ? (
                          <span className="admin-badge">Super</span>
                        ) : isAdminRole(user) ? (
                          <span className="admin-badge">Admin</span>
                        ) : (
                          <span className="user-badge">User</span>
                        )}
                      </td>
                      <td>
        <button
          onClick={() => {
            setUserDetailModalUser(user);
            setEditingUserPlanId(user.planId || PLAN_IDS.BASIC);
          }}
          className="btn-small btn-primary"
          title="View details and manage user"
        >
          View
        </button>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'players' && (
          <div className="admin-section card-ui">
            <h2 className="admin-section-title">All Players</h2>
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>First name</th>
                    <th>Last name</th>
                    <th>Email</th>
                    <th>Skill Level</th>
                    <th>Experience</th>
                    <th>Singles</th>
                    <th>Doubles</th>
                    <th>Apartment</th>
                    <th>Flat</th>
                    <th>Admin</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filterData(players).map(player => {
                    const reg = getRegistrationInfo(player.id);
                    const { firstName, lastName } = getFirstLast(player);
                    const aptFlat = getApartmentFlatDisplay(player);
                    const gameProfile = getProfileForGame(player, adminGameId);
                    return (
                    <tr key={player.id}>
                      <td>
                        <span>{firstName}</span>
                        {aptFlat && <span style={{ display: 'block', fontSize: '0.85em', color: '#666', marginTop: '2px' }}>{aptFlat}</span>}
                      </td>
                      <td>{lastName}</td>
                      <td>{player.email}</td>
                      <td>
                        <span className={`skill-badge skill-${gameProfile.skillLevel || 'average'}`}>
                          {formatSkillLevel(gameProfile.skillLevel)}
                        </span>
                      </td>
                      <td>{gameProfile.yearsOfExperience ?? 0} years</td>
                      <td className="admin-reg-info">{reg.singles}</td>
                      <td className="admin-reg-info">{reg.doubles}</td>
                      <td className="admin-reg-info">{player.apartmentName || '—'}</td>
                      <td>{player.flatNumber || '—'}</td>
                      <td>
                        {player.isAdmin === 'super' ? (
                          <span className="admin-badge">Super</span>
                        ) : isAdminRole(player) ? (
                          <span className="admin-badge">Admin</span>
                        ) : (
                          <span className="user-badge">User</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => openEditProfile(player)}
                            className="btn-small btn-secondary"
                            title="Edit display name, skill level, years of experience"
                          >
                            Edit profile
                          </button>
                          <button
                            onClick={() => openEditRegistration(player)}
                            className="btn-small btn-secondary"
                            title="Change singles/doubles registration for a tournament"
                          >
                            Edit reg
                          </button>
                          {canManageAdmins && (
                            <button
                              onClick={() => toggleAdminStatus(player.id, isAdminRole(player))}
                              className={`btn-small ${isAdminRole(player) ? 'btn-warning' : 'btn-success'}`}
                            >
                              {isAdminRole(player) ? 'Remove Admin' : 'Make Admin'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'pending' && (() => {
          const pending = pendingForGame;
          const byKey = {};
          pending.forEach(r => {
            const key = `${r.tournamentId}-${r.playerId}`;
            if (!byKey[key]) {
              byKey[key] = { tournamentId: r.tournamentId, playerId: r.playerId, playerEmail: r.playerEmail, regs: [], types: [] };
            }
            byKey[key].regs.push(r);
            if (!byKey[key].types.includes(r.type)) byKey[key].types.push(r.type);
          });
          const rows = Object.values(byKey);
          const tournamentById = Object.fromEntries(tournaments.map(t => [t.id, t]));
          return (
            <div className="admin-section card-ui">
              <h2>Pending registrations</h2>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Approve registrations so users can be placed in matches, or reject to remove them from the tournament.
              </p>
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Tournament</th>
                      <th>Player</th>
                      <th>Email</th>
                      <th>Types</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(row => (
                      <tr key={`${row.tournamentId}-${row.playerId}`}>
                        <td>{tournamentById[row.tournamentId]?.name || row.tournamentId}</td>
                        <td>{row.playerEmail?.split('@')[0] || row.playerId}</td>
                        <td>{row.playerEmail || '—'}</td>
                        <td>{row.types.join(', ')}</td>
                        <td>
                          <button
                            onClick={async () => {
                              try {
                                for (const r of row.regs) {
                                  await updateDoc(doc(db, 'registrations', r.id), { status: 'approved' });
                                  await logAudit(AUDIT_ACTIONS.UPDATE, AUDIT_ENTITIES.REGISTRATION, r.id, auditContext(), { status: 'approved' }, { tournamentId: row.tournamentId });
                                }
                                await fetchAllData();
                              } catch (e) {
                                showAlert('Failed to approve: ' + (e.message || e), { variant: 'error' });
                              }
                            }}
                            className="btn-small btn-primary"
                            style={{ marginRight: '8px' }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={async () => {
                              const ok = await showConfirmPromise('Reject this registration? The user will be removed from the tournament.', { title: 'Reject registration', destructive: true });
                              if (!ok) return;
                              try {
                                for (const r of row.regs) {
                                  await deleteDoc(doc(db, 'registrations', r.id));
                                  await logAudit(AUDIT_ACTIONS.DELETE, AUDIT_ENTITIES.REGISTRATION, r.id, auditContext(), null, { tournamentId: row.tournamentId });
                                }
                                await fetchAllData();
                              } catch (e) {
                                showAlert('Failed to reject: ' + (e.message || e), { variant: 'error' });
                              }
                            }}
                            className="btn-small btn-danger"
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length === 0 && (
                  <p style={{ marginTop: '12px', color: '#666' }}>No pending registrations.</p>
                )}
              </div>
            </div>
          );
        })()}

        {activeTab === 'apartments' && (
          <div className="admin-section card-ui">
            <h2>Registered apartments</h2>
            <p style={{ marginBottom: '16px', color: '#666' }}>
              Apartments and flat numbers users can choose after login. Use the range utility or comma-separated list below.
            </p>
            <div style={{ maxWidth: '620px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Apartment name</span>
                <input
                  type="text"
                  value={apartmentName}
                  onChange={(e) => setApartmentName(e.target.value)}
                  placeholder="e.g. Tower A"
                  className="search-input"
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Short code</span>
                <input
                  type="text"
                  value={apartmentShortCode}
                  onChange={(e) => setApartmentShortCode(e.target.value)}
                  placeholder="e.g. TA, BLK-A (optional)"
                  className="search-input"
                  style={{ maxWidth: '200px' }}
                />
              </label>
              <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <span style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Range utility</span>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>
                  Comma-separated. Numeric: <code>003-030</code> → 003,…,030. Alphanumeric: <code>G1-G15</code>, <code>A1-101</code>. 2D: <code>A1-101 until A2-132</code>.
                </p>
                <input
                  type="text"
                  value={rangeExpression}
                  onChange={(e) => setRangeExpression(e.target.value)}
                  placeholder="e.g. 003-030, 101-132, G1-G15 or A1-101 until A2-132"
                  className="search-input"
                  style={{ marginBottom: '8px', marginTop: '8px' }}
                />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={handleAddFromRange} className="btn-secondary" disabled={!rangeExpression.trim()}>
                    Add from range
                  </button>
                  <button type="button" onClick={handleReplaceWithRange} className="btn-secondary" disabled={!rangeExpression.trim()}>
                    Replace with range
                  </button>
                </div>
              </div>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Flat numbers (comma-separated or use range above)</span>
                <input
                  type="text"
                  value={apartmentFlats}
                  onChange={(e) => setApartmentFlats(e.target.value)}
                  placeholder="e.g. 101, 102, 103 or use range utility"
                  className="search-input"
                />
              </label>
              <div>
                <span style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Current flat numbers — add, remove, or edit</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '8px' }}>
                  {getFlatList().map((f, i) => (
                    <span
                      key={`${i}-${f}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 8px',
                        background: editingFlatIndex === i ? '#fff' : '#e9ecef',
                        border: '1px solid #ced4da',
                        borderRadius: '6px',
                        fontSize: '13px'
                      }}
                    >
                      {editingFlatIndex === i ? (
                        <input
                          type="text"
                          value={editingFlatValue}
                          onChange={(e) => setEditingFlatValue(e.target.value)}
                          onBlur={saveEditFlat}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveEditFlat(); }}
                          autoFocus
                          style={{ width: '72px', padding: '2px 6px', fontSize: '13px' }}
                        />
                      ) : (
                        <span onClick={() => startEditFlat(i)} style={{ cursor: 'pointer' }} title="Click to edit">{f}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveFlat(i)}
                        aria-label="Remove"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '16px', lineHeight: 1, color: '#6c757d' }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={singleFlatToAdd}
                    onChange={(e) => setSingleFlatToAdd(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSingleFlat(); } }}
                    placeholder="Add one flat"
                    className="search-input"
                    style={{ width: '140px' }}
                  />
                  <button type="button" onClick={handleAddSingleFlat} className="btn-secondary" disabled={!singleFlatToAdd.trim()}>
                    Add flat
                  </button>
                </div>
              </div>
              <div style={{ marginTop: '16px' }}>
                <span style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Profile &amp; contact (optional)</span>
                <EntityProfileFields
                  value={apartmentProfileFields}
                  onChange={(key, val) => setApartmentProfileFields((p) => ({ ...p, [key]: val }))}
                  avatarAlt="Apartment"
                />
              </div>
              <div style={{ marginTop: '16px' }}>
                <span style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px' }}>Apartment admins</span>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#555' }}>
                  Users who can approve others to join this apartment. Add by email.
                </p>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <input
                    type="email"
                    value={apartmentAdminEmailToAdd}
                    onChange={(e) => setApartmentAdminEmailToAdd(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addApartmentAdminByEmail())}
                    placeholder="Email to add as apartment admin"
                    className="search-input"
                    style={{ width: '240px' }}
                  />
                  <button type="button" onClick={addApartmentAdminByEmail} className="btn-secondary" disabled={!apartmentAdminEmailToAdd.trim()}>
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {editingApartmentAdminIds.map(uid => {
                    const u = users.find(x => x.id === uid);
                    return (
                      <span key={uid} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#e9ecef', borderRadius: '6px', fontSize: '13px' }}>
                        {u?.email || uid}
                        <button type="button" onClick={() => removeApartmentAdmin(uid)} aria-label="Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '16px', lineHeight: 1, color: '#6c757d' }}>×</button>
                      </span>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={saveApartment} className="btn-primary" disabled={apartmentSaving}>
                  {apartmentSaving ? 'Saving…' : editingApartmentId ? 'Update' : 'Add apartment'}
                </button>
                {editingApartmentId && (
                  <button onClick={() => { setEditingApartmentId(null); setApartmentName(''); setApartmentShortCode(''); setApartmentFlats(''); setEditingApartmentAdminIds([]); setApartmentProfileFields({ avatarURL: '', description: '', phone: '', address: '', mapUrl: '' }); setRangeExpression(''); setSingleFlatToAdd(''); setEditingFlatIndex(null); }} className="btn-secondary">
                    Cancel
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Apartment</th>
                    <th>Short code</th>
                    <th>Flat numbers</th>
                    <th>Admins</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apartments.map(apt => (
                    <tr key={apt.id}>
                      <td>{apt.name}</td>
                      <td>{apt.shortCode || '—'}</td>
                      <td>{(apt.flatNumbers || []).join(', ')}</td>
                      <td>{(apt.adminIds || []).length}</td>
                      <td>
                        <button onClick={() => startEditApartment(apt)} className="btn-small btn-secondary" style={{ marginRight: '8px' }}>Edit</button>
                        <button onClick={() => deleteApartment(apt.id)} className="btn-small btn-danger">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {apartments.length === 0 && (
                <p style={{ marginTop: '12px', color: '#666' }}>No apartments yet. Add one above.</p>
              )}
            </div>
          </div>
        )}

      </div>

      {userDetailModalUser && (
        <div
          className="admin-section card-ui"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setUserDetailModalUser(null)}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            {(() => {
              const user = userDetailModalUser;
              const { firstName, lastName } = getFirstLast(user);
              const fullName = [firstName, lastName].filter(Boolean).join(' ').trim() || user.displayName || user.email || '—';
              const approved = user.apartmentApproved === true || user.apartmentApproved === 'true';
              const matchCount = getMatchCountForUser(user.id);
              const tournamentById = Object.fromEntries(tournaments.map(t => [t.id, t]));
              const userRegs = registrations.filter(r => r.playerId === user.id);
              return (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>User details</h3>
                    <button type="button" onClick={() => setUserDetailModalUser(null)} className="btn-secondary" style={{ padding: '6px 12px' }} aria-label="Close">×</button>
                  </div>
                  <dl style={{ margin: '0 0 20px 0', display: 'grid', gap: '8px', fontSize: '14px' }}>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Name</dt><dd style={{ margin: 0 }}>{fullName}</dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Email</dt><dd style={{ margin: 0 }}>{user.email || '—'}</dd></div>
                    <div>
                      <dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Plan</dt>
                      <dd style={{ margin: 0 }}>
                        <select
                          value={editingUserPlanId ?? user.planId ?? PLAN_IDS.FREE}
                          onChange={(e) => updateUserPlan(user.id, e.target.value)}
                          disabled={savingUserPlan}
                          className="search-input"
                          style={{ maxWidth: '160px', padding: '6px 10px' }}
                        >
                          {PLANS.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                        {savingUserPlan && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>Saving…</span>}
                      </dd>
                    </div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Onboarded</dt><dd style={{ margin: 0 }}>{user.onboarded ? '✓ Yes' : '✗ No'}</dd></div>
                    {user.loginBlocked && <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Status</dt><dd style={{ margin: 0, color: '#c00' }}>Login blocked</dd></div>}
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Skill level ({adminGameId || 'table-tennis'})</dt><dd style={{ margin: 0 }}><span className={`skill-badge skill-${getProfileForGame(user, adminGameId).skillLevel || 'average'}`}>{formatSkillLevel(getProfileForGame(user, adminGameId).skillLevel)}</span></dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Experience</dt><dd style={{ margin: 0 }}>{getProfileForGame(user, adminGameId).yearsOfExperience ?? 0} years</dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Apartment</dt><dd style={{ margin: 0 }}>{user.apartmentName || '—'}</dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Unit</dt><dd style={{ margin: 0 }}>{getApartmentFlatDisplay(user) || user.flatNumber || '—'}</dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Apartment approved</dt><dd style={{ margin: 0 }}>{user.apartmentId ? (approved ? '✓ Yes' : '— No') : '—'}</dd></div>
                    <div><dt style={{ fontWeight: 600, color: '#666', marginBottom: '2px' }}>Matches</dt><dd style={{ margin: 0 }}>{matchCount} match{matchCount !== 1 ? 'es' : ''}</dd></div>
                  </dl>
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>Registrations</h4>
                    {userRegs.length === 0 ? (
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>None</p>
                    ) : (
                      <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                        {userRegs.map(r => {
                          const t = tournamentById[r.tournamentId];
                          return (
                            <li key={r.id}>{t?.name || r.tournamentId} — {r.type === 'singles' ? 'Singles' : 'Doubles'}{r.status ? ` (${r.status})` : ''}</li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const profile = {
                          displayName: user.displayName || user.email || 'Unknown',
                          email: user.email || '',
                          photoURL: user.photoURL || user.avatarURL || null,
                        };
                        startImpersonating(user.id, profile);
                        setUserDetailModalUser(null);
                        navigate('/');
                      }}
                      className="btn-small btn-primary"
                      title="Login as this user – view app as them across all pages"
                    >
                      Login as
                    </button>
                    <button type="button" onClick={() => { openEditProfile(user); setUserDetailModalUser(null); }} className="btn-small btn-secondary">Edit profile</button>
                    <button type="button" onClick={() => { openEditRegistration(user); setUserDetailModalUser(null); }} className="btn-small btn-secondary">Edit registrations</button>
                    {user.apartmentId && (
                      <button type="button" onClick={() => { toggleApartmentApproved(user.id, approved); setUserDetailModalUser(null); }} className={`btn-small ${approved ? 'btn-warning' : 'btn-success'}`}>
                        {approved ? 'Revoke approval' : 'Approve'}
                      </button>
                    )}
                    {roleIsAdmin && user.id !== currentUser?.uid && !user.loginBlocked && (
                      <button type="button" onClick={async () => { await blockUser(user.id); setUserDetailModalUser(null); }} className="btn-small btn-warning" title="Block this user from signing in. They will see a blocked message.">
                        Block login
                      </button>
                    )}
                    {canManageAdmins && (
                      <>
                        <button type="button" onClick={() => { toggleAdminStatus(user.id, isAdminRole(user)); setUserDetailModalUser(null); }} className={`btn-small ${isAdminRole(user) ? 'btn-warning' : 'btn-success'}`}>
                          {isAdminRole(user) ? 'Remove Admin' : 'Make Admin'}
                        </button>
                        {user.id !== currentUser?.uid && (
                          <button type="button" onClick={async () => { await deleteUser(user.id); setUserDetailModalUser(null); }} className="btn-small btn-danger">Delete</button>
                        )}
                      </>
                    )}
                    <button type="button" onClick={() => setUserDetailModalUser(null)} className="btn-small btn-secondary">Close</button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {editingRegUser && (
        <div
          className="admin-section card-ui"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeEditRegistration}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Edit registration</h3>
            <p style={{ margin: '0 0 12px 0', color: '#555', fontSize: '14px' }}>
              {editingRegUser.displayName || editingRegUser.email} — choose tournament and registration types.
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Tournament</label>
              <select
                value={editingRegTournamentId}
                onChange={e => {
                  const tid = e.target.value;
                  setEditingRegTournamentId(tid);
                  const regs = registrations.filter(
                    r => r.playerId === editingRegUser.id && r.tournamentId === tid
                  );
                  setEditingRegSingles(regs.some(r => r.type === 'singles'));
                  setEditingRegDoubles(regs.some(r => r.type === 'doubles'));
                }}
                className="search-input"
                style={{ width: '100%' }}
              >
                <option value="">— Select tournament —</option>
                {tournamentsForGame.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: '16px', display: 'flex', gap: '16px' }}>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  checked={editingRegSingles}
                  onChange={e => setEditingRegSingles(e.target.checked)}
                />
                Singles
              </label>
              <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <input
                  type="checkbox"
                  checked={editingRegDoubles}
                  onChange={e => setEditingRegDoubles(e.target.checked)}
                />
                Doubles
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeEditRegistration} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveUserRegistration}
                disabled={editingRegSaving || (!editingRegSingles && !editingRegDoubles)}
                className="btn-primary"
              >
                {editingRegSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingProfileUser && (
        <div
          className="admin-section card-ui"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeEditProfile}
        >
          <div
            style={{
              background: '#fff',
              padding: '24px',
              borderRadius: '12px',
              maxWidth: '420px',
              width: '90%',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0' }}>Edit user profile</h3>
            <p style={{ margin: '0 0 12px 0', color: '#555', fontSize: '14px' }}>
              Editing profile for <strong>{editingProfileUser.displayName || editingProfileUser.email || editingProfileUser.id}</strong>. Changes are logged and visible to the user.
            </p>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Display name</label>
              <input
                type="text"
                value={profileEditDisplayName}
                onChange={e => setProfileEditDisplayName(e.target.value)}
                placeholder="Display name"
                className="search-input"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Skill level</label>
              <select
                value={profileEditSkillLevel}
                onChange={e => setProfileEditSkillLevel(e.target.value)}
                className="search-input"
                style={{ width: '100%' }}
              >
                <option value="beginner">Beginner</option>
                <option value="average">Average</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '600' }}>Years of experience</label>
              <input
                type="number"
                min={0}
                max={50}
                value={profileEditYears}
                onChange={e => setProfileEditYears(e.target.value)}
                placeholder="0–50"
                className="search-input"
                style={{ width: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeEditProfile} className="btn-secondary">
                Cancel
              </button>
              <button
                type="button"
                onClick={saveProfileEdit}
                disabled={profileEditSaving}
                className="btn-primary"
              >
                {profileEditSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

